<template>
  <section class="[ max-w-5xl mx-auto ]">
    <div class="[ flex gap-3 items-center ]">
      <DateRangeRadio
        v-model="selectedDateRangeOption"
        :range-options="dateRangeOptions"
        :intervals="dateRangeOptionLabels"
        @custom-date-selected="setCustomDateRangeValue"
      />
    </div>

    <div class="[ border-y py-4 my-4 text-sm font-bold ]">
      <div>{{ readableDateFilter }}</div>
    </div>

    <div v-if="!leaderboardData.length && loadingLeaderboard" class="[ my-4 ]">
      <JBLoadingSpinner />
    </div>
    <div v-else-if="!leaderboardData.length" class="[ my-4 ]">
      <Empty
        icon="info-circle"
        title="No activity"
        message="No agents have participated in the selected time range."
      />
    </div>
    <div
      v-else
      class="[ flex flex-col gap-3 ]"
      :class="{ 'opacity-50': loadingLeaderboard }"
    >
      <LeaderboardEntry
        :entry="agent"
        v-for="(agent, index) in leaderboardData"
        :key="`agent_${index}`"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useLeaderboard } from "@/composables/leaderboard.ts";
import { add, format } from "date-fns";
import LeaderboardEntry from "@/components/agents/agent/LeaderboardEntry.vue";
import JBLoadingSpinner from "@/components/ui/JBLoadingSpinner.vue";
import Empty from "@/components/ui/Empty.vue";
import DateRangeRadio from "@/components/contact/qa/DateRangeRadio.vue";

definePage({
  meta: {
    title: "Leaderboard",
    icon: "champion",
    scope: "Team.Read.TeamManager",
  },
});

const now = new Date(Date.now());
const selectedDateRangeOption = ref<number>(0);
const dateRangeOptionLabels: string[] = ["1W", "1M", "3M", "6M", "1Y"];
const isCustomDateRangeSelected = computed<boolean>(() => {
  const [start, end] = customDateRange.value;
  return !!(start && end);
});
const dateRangeOptions: [Date, Date][] = [
  [add(now, { weeks: -1 }), now],
  [add(now, { months: -1 }), now],
  [add(now, { months: -3 }), now],
  [add(now, { months: -6 }), now],
  [add(now, { years: -1 }), now],
];

const customDateRange = ref<[Date | null, Date | null]>([null, null]);

const selectedDateRange = computed<[Date, Date]>(() => {
  if (isCustomDateRangeSelected.value) {
    const [start, end] = customDateRange.value;
    if (start && end) {
      return [start, end];
    }
  }

  return dateRangeOptions[selectedDateRangeOption.value];
});

const { loadingLeaderboard, leaderboardData } =
  useLeaderboard(selectedDateRange);

const readableDateFilter = computed<string>(() => {
  const selectedDates = isCustomDateRangeSelected.value
    ? customDateRange.value
    : selectedDateRange.value;

  return selectedDates
    .filter((date) => date)
    .map((date) => format(date, "MMM dd yyyy"))
    .join(" - ");
});

function setCustomDateRangeValue(
  v: Date | (Date | null)[] | Date[] | null | undefined,
) {
  customDateRange.value = v as [Date, Date];
}
</script>
